import { Component, EventEmitter, Input, Optional, Output } from "@angular/core";
import { NzModalRef } from "ng-zorro-antd/modal";
import { NzTooltipDirective } from "ng-zorro-antd/tooltip";
import { AssessmentAssignmentRole, OrganizationRole, UserAccessContext } from "../../services/assessment_user_access.types";
import { ASSIGNMENT_ROLE_CHOICES, assignmentSelectionError } from "./assessment_assignment_form.util";

@Component({
    selector: "app-assessment-assignment-form",
    template: `
        <fieldset [disabled]="disabled || !!assessment?.readOnly">
            <legend>Roles on this assessment</legend>
            <div *ngFor="let choice of choices">
                <label [class.assignment-choice--disabled]="disabled || !!assessment?.readOnly || (!roles.includes(choice.value) && !!unavailableReason(choice.value))"><input type="checkbox" [checked]="roles.includes(choice.value)"
                    [disabled]="disabled || !!assessment?.readOnly || (!roles.includes(choice.value) && !!unavailableReason(choice.value))"
                    (change)="toggle(choice.value, $event)" />
                    <span nz-tooltip #roleTip="nzTooltip" [nzTooltipTitle]="roleTooltip(choice.value)" [nzTooltipOverlayStyle]="tooltipStyle"
                        (nzTooltipVisibleChange)="raiseTooltip(roleTip, $event)">{{ choice.label }} <span *ngIf="roleTooltip(choice.value)" nz-icon nzType="info-circle" aria-hidden="true"></span></span>
                </label>
                <small class="cb4-dialog-error" *ngIf="!roles.includes(choice.value) && unavailableReason(choice.value) as reason">{{ reason }}</small>
            </div>
        </fieldset>
        <p *ngIf="error" role="status" [class.cb4-dialog-note]="required && !roles.length && !assessment?.readOnly"
            [class.cb4-dialog-error]="!(required && !roles.length && !assessment?.readOnly)">{{ error }}</p>
    `,
    styles: [`
        fieldset { border: 1px solid #ddd; padding: 12px; margin-top: 16px; }
        legend { font-size: 1rem; }
        label { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
        input { margin: 0; flex: none; }
        .assignment-choice--disabled, .assignment-choice--disabled * { cursor: not-allowed; }
        small { display: block; margin-left: 20px; }
    `]
})
export class AssessmentAssignmentFormComponent {
    readonly tooltipStyle: Record<string, string>;
    constructor(@Optional() modal: NzModalRef | null = null) {
        this.tooltipStyle = { 'z-index': String(Math.max(1070, Number(modal?.getConfig().nzZIndex || 1000) + 10)) };
    }
    raiseTooltip(tooltip: NzTooltipDirective, visible: boolean): void {
        if (!visible) return;
        // The connected overlay is attached after the visibility event. Its
        // positioning host forms a separate stacking context above the modal.
        queueMicrotask(() => {
            const overlay = tooltip.component?.overlay?.overlayRef;
            if (overlay && tooltip.component?.nzVisible) overlay.hostElement.style.zIndex = this.tooltipStyle['z-index'];
        });
    }
    @Input() assessment: UserAccessContext["assessment"] = null;
    @Input() roles: AssessmentAssignmentRole[] = [];
    @Input() organizationRole: OrganizationRole = "User";
    @Input() targetUserId: number | null = null;
    @Input() required = false;
    @Input() disabled = false;
    @Output() rolesChange = new EventEmitter<AssessmentAssignmentRole[]>();
    readonly choices = ASSIGNMENT_ROLE_CHOICES;
    roleTooltip(role: AssessmentAssignmentRole): string | null {
        if (role === "readers") return "These users can see assessment responses and upload files to the merchant's Secure Storage, including requested evidence. They cannot change assessment responses, see or request generated PDFs, or see assessment notes.";
        if (role === "merchant_rep") return "This user can see assessment responses but cannot make changes except to the control asking for a signature from a representative from the assessment's merchant or service provider.  This user can also preview and print generated PDFs but cannot request to generate new ones. This user will also not be able to see assessment notes.";
        return null;
    }
    get error(): string { return assignmentSelectionError(this.assessment, this.roles, this.organizationRole, this.targetUserId, this.required); }
    unavailableReason(role: AssessmentAssignmentRole): string {
        return assignmentSelectionError(this.assessment, [...this.roles, role], this.organizationRole, this.targetUserId, false);
    }
    toggle(role: AssessmentAssignmentRole, event: Event): void {
        if (this.disabled || this.assessment?.readOnly || !this.assessment) return;
        const checked = (event.target as HTMLInputElement).checked;
        if (checked && this.unavailableReason(role)) return;
        this.rolesChange.emit(checked ? [...new Set([...this.roles, role])] : this.roles.filter((value) => value !== role));
    }
}
